import { render } from "preact";
import { h } from "preact";
import App from "./App";
import { widgetCss } from "./styles";
import { parseDefaultTab, supportEnabledFor, type WidgetBrand, type WidgetConfig, type WidgetSide } from "./data";
import { setAuthToken, setTokenProvider, type TokenProvider } from "./viewer";

// Bootstrap: read the host <script> tag's data-* config, mount the widget into
// an isolated shadow root. Loaded via a single tag on any partner site:
//
//   <script src=".../knowledge-base/widget.js"
//     data-kb-url="https://supabase.dev.skymind.com"
//     data-anon-key="<anon>"
//     data-app="validaide-control-intervention"
//     data-token="<per-app share token>"   (optional; unlocks all published)
//     data-brand="validaide"                (optional; validaide|skycell)
//     data-context="cni:shipments"          (optional; overrides URL relevance)
//     data-launcher="none"                  (optional; hide the floating button)
//     data-launcher-label="Help"            (optional; button text)
//                                            default "Knowledge Base"
//     data-title="Knowledge Base"           (optional; panel header text)
//     data-side="left"                      (optional; left|right dock edge)
//     data-support="none"                   (optional; hide the in-panel
//                                            Contact Support form, which is
//                                            offered for data-app="validaide"
//                                            only — see supportEnabledFor)
//     data-standalone-url="https://…/help"  (optional; overrides the derived
//                                            standalone-app link in the footer.
//                                            A Validaide embed needs none: the
//                                            server reports that host and the
//                                            widget prefers it.)
//     data-auth-token="<host access token>" (optional; the reader's Keycloak
//                                            token, so the list follows their
//                                            entitlement roles. A token rotates,
//                                            so a real integration registers
//                                            setTokenProvider() instead — this
//                                            attribute is for a demo page.)
//     data-default-tab="all"                (optional; open on All Articles
//                                            instead of Relevant Articles.
//                                            data-app="validaide" opens on All
//                                            unless this says "relevant": its
//                                            relevance is not mapped yet.)
//     data-folders="none"                   (optional; flat list, no folders)
//     data-expanded-width="1440"            (optional; expanded panel width px)
//     defer></script>
//
// Button label and panel header are separate on purpose: the launcher lives in
// the host's chrome and should read as that host's help affordance ("Help"),
// while the panel header names what opened ("Knowledge Base").
//
// Hosts that trigger the widget from their own UI (a menu entry, a help button)
// set data-launcher="none" and call the global API instead:
//
//   window.SkyCellKB.open() / .close() / .toggle()
//
// A Keycloak-SSO'd host also tells the widget WHO is reading, so role-gated
// articles stay with the roles that hold them. One call, at init, and nothing
// to maintain afterwards — the widget asks before each read, so the host can
// refresh on demand instead of racing the expiry, and a sign-out is just a null:
//
//   window.SkyCellKB.setTokenProvider(() =>
//     keycloak.updateToken(30).then(() => keycloak.token));
//
// setAuthToken(keycloak.token) — pushing each token as it rotates — still works
// for the hosts that integrated before the provider existed.
//
// open() also takes an optional article slug — open("some-slug") opens the
// panel straight onto that article. The same works link-shareable via the host
// page's URL hash: #skycell-kb=<slug> (parsed in App.tsx, see deeplink.ts).
//
// "Relevant Articles" needs no host integration: it derives from the host page's
// own URL (see relevance.ts). data-context / setContext() are an OPTIONAL
// override for hosts whose routes are opaque, or that want exactness.
//
// The API is event-based (CustomEvents on window) so it crosses the shadow-root
// boundary without the host holding any reference to the widget internals.

const MOUNT_ID = "skycell-kb-widget-root";

function resolveScript(): HTMLScriptElement | null {
  // document.currentScript works for a classic, non-module script during its
  // own execution. Fall back to locating our tag by src for odd loaders.
  const cur = document.currentScript as HTMLScriptElement | null;
  if (cur && cur.dataset && "kbUrl" in cur.dataset) return cur;
  const bySrc = document.querySelector<HTMLScriptElement>('script[src*="widget.js"][data-kb-url]');
  return bySrc ?? cur;
}

/**
 * The KB app's own base URL. The widget bundle is served by that app at
 * <host><base>/widget.js, so stripping the filename leaves <host><base>/ — the
 * root the ask endpoint, the support endpoint and the standalone article links
 * all hang off. Derived, never configured, so a host can't point the endpoints
 * somewhere else by mistake. Undefined for an unparseable/relative src.
 */
function deriveAppBase(script: HTMLScriptElement | null): string | undefined {
  try {
    const src = new URL(script?.src ?? "", window.location.href);
    return src.href.replace(/[^/]*$/, "");
  } catch {
    return undefined;
  }
}

/** Resolve `path` against the derived app base. */
function fromAppBase(base: string | undefined, path: string): string | undefined {
  if (!base) return undefined;
  try {
    return new URL(path, base).href;
  } catch {
    return undefined;
  }
}

/**
 * data-support: the in-panel Contact Support form.
 *
 * Opt-out for the one app that has it. The form mails Validaide product
 * support, so only a support-enabled app (`validaide`) derives the endpoint at
 * all; every other embed gets `undefined` and shows no contact affordance,
 * because its host product takes help requests through its own Jira
 * service-desk widget. data-support="none" turns it off for a Validaide embed
 * too — e.g. a page that already has its own support entry point.
 */
function parseSupportUrl(
  raw: string | undefined,
  app: string,
  appBase: string | undefined,
): string | undefined {
  if (raw?.trim() === "none" || !supportEnabledFor(app)) return undefined;
  return fromAppBase(appBase, "api/support/embed");
}

/**
 * data-standalone-url: the standalone app the footer's "Open in the Knowledge
 * Base" link points at. Defaults to the derived app base — the embedded KB app
 * is itself a full reading surface — and is overridden for deployments with a
 * dedicated per-app help host (docs/guides/STANDALONE-HELP-SITES.md). Normalized to a
 * trailing slash so `kb/<slug>` resolves against it rather than replacing its
 * last path segment.
 */
function parseStandaloneUrl(
  raw: string | undefined,
  appBase: string | undefined,
): string | undefined {
  const trimmed = (raw ?? "").trim();
  if (!trimmed) return appBase;
  try {
    const url = new URL(trimmed, window.location.href);
    if (!url.pathname.endsWith("/")) url.pathname += "/";
    return url.href;
  } catch {
    return appBase;
  }
}

function readConfig(script: HTMLScriptElement | null): WidgetConfig | null {
  const d = script?.dataset ?? {};
  const kbUrl = d.kbUrl?.trim();
  const anonKey = d.anonKey?.trim();
  const app = d.app?.trim();
  if (!kbUrl || !anonKey || !app) {
    // eslint-disable-next-line no-console
    console.error(
      "[skycell-kb-widget] missing required data-* attributes: " +
        "data-kb-url, data-anon-key and data-app are all required.",
    );
    return null;
  }
  const brand: WidgetBrand = d.brand?.trim() === "skycell" ? "skycell" : "validaide";
  const side: WidgetSide = d.side?.trim() === "left" ? "left" : "right";
  const appBase = deriveAppBase(script);
  return {
    kbUrl,
    anonKey,
    app,
    token: d.token?.trim() || undefined,
    brand,
    context: parseContext(d.context),
    launcher: d.launcher?.trim() !== "none",
    launcherLabel: d.launcherLabel?.trim() || "Knowledge Base",
    title: d.title?.trim() || "Knowledge Base",
    side,
    configUrl: fromAppBase(appBase, "api/config"),
    supportUrl: parseSupportUrl(d.support, app, appBase),
    standaloneUrl: parseStandaloneUrl(d.standaloneUrl, appBase),
    standaloneUrlExplicit: Boolean(d.standaloneUrl?.trim()),
    folders: d.folders?.trim() === "none" ? "none" : "tiles",
    defaultTab: parseDefaultTab(d.defaultTab, app),
    expandedWidth: parseWidth(d.expandedWidth) ?? 1200,
    askUrl: fromAppBase(appBase, "api/ask/embed"),
    // Trailing slash: the read calls append "articles" / "article".
    readUrl: fromAppBase(appBase, "api/embed/"),
  };
}

/** data-expanded-width, in px. Ignored unless it's a sane positive number. */
function parseWidth(raw: string | undefined): number | null {
  const n = Number.parseInt((raw ?? "").trim(), 10);
  return Number.isFinite(n) && n >= 320 ? n : null;
}

/** "cni:shipments, cni:lanes" → ["cni:shipments", "cni:lanes"]. */
function parseContext(raw: string | string[] | undefined): string[] {
  const list = Array.isArray(raw) ? raw : (raw ?? "").split(",");
  return list.map((s) => String(s).trim()).filter(Boolean);
}

// Programmatic control for hosts that hide the launcher (data-launcher="none").
// Dispatched as window CustomEvents; App.tsx listens (see KB_EVENTS there).
declare global {
  interface Window {
    SkyCellKB?: {
      open: (slug?: string) => void;
      close: () => void;
      toggle: () => void;
      setContext: (context: string | string[]) => void;
      setTokenProvider: (provider: TokenProvider | null) => void;
      setAuthToken: (token: string | null) => void;
    };
  }
}

function installApi() {
  const fire = (name: string) => () => window.dispatchEvent(new CustomEvent(name));
  window.SkyCellKB = {
    open: (slug?: string) =>
      window.dispatchEvent(
        new CustomEvent("skycell-kb:open", { detail: slug ? { slug } : undefined }),
      ),
    close: fire("skycell-kb:close"),
    toggle: fire("skycell-kb:toggle"),
    // Keycloak-SSO'd hosts name the reader, which is what makes the article
    // list follow their entitlement roles. Register a provider once and the
    // widget pulls a current token before each read; setAuthToken is the older
    // push path, ignored once a provider exists (see ./viewer.ts).
    setTokenProvider,
    setAuthToken,
    // SPA hosts re-point the Relevant tab as the user moves between modules.
    // Callable before the widget has mounted (the value is buffered) and while
    // the panel is closed.
    setContext: (context: string | string[]) => {
      pendingContext = parseContext(context);
      window.dispatchEvent(
        new CustomEvent("skycell-kb:context", { detail: { context: pendingContext } }),
      );
    },
  };
}

// A setContext() call that lands before the widget mounts (the script is
// deferred; the host's router may be faster) would otherwise be lost — the
// CustomEvent has no listener yet. Buffer it and mount with it instead.
let pendingContext: string[] | null = null;

function mount(base: WidgetConfig) {
  if (document.getElementById(MOUNT_ID)) return; // idempotent
  const config = pendingContext ? { ...base, context: pendingContext } : base;
  const host = document.createElement("div");
  host.id = MOUNT_ID;
  document.body.appendChild(host);

  const shadow = host.attachShadow({ mode: "open" });
  const style = document.createElement("style");
  style.textContent = widgetCss(config.brand);
  shadow.appendChild(style);
  const app = document.createElement("div");
  // color/font anchor inside the shadow tree (see .kb-scope in styles.ts).
  app.className = "kb-scope";
  shadow.appendChild(app);

  render(h(App, { config }), app);
}

function start() {
  const script = resolveScript();
  const config = readConfig(script);
  if (!config) return;
  installApi();
  // A host that can render the token into the page may do so instead of calling
  // setAuthToken. Applied before mount so the first list is already filtered.
  setAuthToken(script?.dataset.authToken);
  if (document.body) mount(config);
  else document.addEventListener("DOMContentLoaded", () => mount(config), { once: true });
}

start();
